/* eslint-disable no-case-declarations */
import { RichTextBox } from "@common/components/richTextBox";
import { getOutOfBoundsDate, getRoundedDate } from "@common/utils/datetimeUtils";
import classNames from "classnames";
import { isEmpty } from "lodash";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useRef } from "react";
import { Form, FormCheckProps, FormControlProps } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, ControllerFieldState, ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { NumericFormat, NumericFormatProps, PatternFormat } from "react-number-format";
import { FormGroup } from "../FormGroup";
import { Patterns } from "../ValidationRules";
import {
	FormDateInputProps,
	FormInputProps,
	FormNumberInputProps,
	FormRichTextInputProps,
	FormTelephoneInputProps,
	FormTextAreaInputProps,
	FormTimeInputProps,
	FormatDateInputSerializationFormat,
} from "./types";

export const spreadAndtransformDefaultValues = <T,>(inputValues: T): T => {
	if (!inputValues) {
		return inputValues;
	}

	const transformValue = (value: any): any => {
		if (value === undefined) {
			return null;
		} else if (Array.isArray(value)) {
			return value.map(item => transformValue(item));
		} else if (value instanceof Date) {
			// was returning {} instead of Date obj
			return value;
		} else if (typeof value === "object") {
			return spreadAndtransformDefaultValues(value);
		} else {
			return value;
		}
	};

	return Object.fromEntries(Object.entries(inputValues).map(([key, value]) => [key, transformValue(value)])) as T;
};
/**
 *
 * @param value "MM/dd/yyyy" | "yyyy-MM-dd"
 * @param serializationFormat "short" | "iso"
 * @returns JSDate
 */
export const deserializeDate = (value: string, serializationFormat: FormatDateInputSerializationFormat | undefined = "iso") => {
	switch (serializationFormat) {
		case "short":
			return DateTime.fromFormat(value, "MM/dd/yyyy").toJSDate();
		case "iso":
			return DateTime.fromFormat(value.substring(0, 10), "yyyy-MM-dd").toJSDate();
		default:
			throw new Error("Unknown Date FormInput serialization format: " + serializationFormat);
	}
};

export const serializeDate = (value: Date, serializationFormat: FormatDateInputSerializationFormat | undefined = "iso") => {
	switch (serializationFormat) {
		case "short":
			return DateTime.fromJSDate(value).toFormat("MM/dd/yyyy");
		case "iso":
			return DateTime.fromJSDate(value).toISODate();
		default:
			throw new Error("Unknown Date FormInput serialization format: " + serializationFormat);
	}
};

export function FormInput<T extends FieldValues>(inputProps: FormInputProps<T>) {
	const {
		fieldName,
		control,
		type,
		label,
		controlId,
		className,
		labelRowJustifyClass = "justify-content-between",
		labelTailElement,
		isRequired,
		helpText,
		rules,
		highlightDirty = true,
		allowEmptyString,
		decimalPlaces,
		labelPosition,
		labelColumnSmWidth,
		inputColumnSmWidth,
		labelColumnMdWidth,
		inputColumnMdWidth,
		inputColumClassName,
		...inputAttrs
	} = inputProps;

	if (isEmpty(label) && isEmpty(controlId)) throw new Error("FormInput requires either a 'label' for internally managed labels or a 'controlId' for externally  managed labels.");

	const resolvedControlId = useMemo(() => (controlId ? controlId : `form-${label.replaceAll(/[\W_]/gi, "").toLowerCase()}`), [label, controlId]);
	const helpId = useMemo(() => `${resolvedControlId}-help`, [resolvedControlId]);
	const errorId = useMemo(() => `${resolvedControlId}-error`, [resolvedControlId]);

	const reverseLabel = type === "checkbox" || type === "radio";

	const resolvedRules = useMemo(() => {
		const newRules = rules ?? {};
		if (isRequired) {
			if (!newRules["required"]) newRules.required = "Required";
		} else {
			newRules.required = rules?.required;
		}
		if (type == "text" || type == "textarea" || type == undefined) {
			if (!newRules["pattern"]) newRules.pattern = Patterns.nonWhiteSpace;
		}
		if (type == "tel" && !(inputAttrs as FormTelephoneInputProps).ignorerules) {
			if (!newRules["pattern"]) newRules.pattern = Patterns.phoneWithDashes;
		}
		return newRules;
	}, [rules, isRequired, type, inputAttrs]);

	const numInputRefs = useRef<HTMLInputElement>();
	const listenersAttached = useRef(false);

	useEffect(() => {
		function cancelWheel(e: Event & { target: HTMLElement }) {
			const { target } = e || {};
			target.blur();
		}
		if (!listenersAttached.current) {
			numInputRefs && numInputRefs.current?.addEventListener("wheel", cancelWheel, { passive: true });
			listenersAttached.current = true;
		}
		
		return () => {
			if(listenersAttached.current){
				numInputRefs.current?.removeEventListener("wheel", cancelWheel, false);
			}
		};
	}, []);

	const removeEmojis = (value: string) => {
		return value.replace(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g, "");
	};

	// choose the
	const renderInput = (field: ControllerRenderProps<T, Path<T>>, fieldState: ControllerFieldState) => {
		const commonClasses = classNames(className, fieldState.invalid && "is-invalid", highlightDirty && fieldState.isDirty && "is-dirty");

		const commonProps = {
			id: resolvedControlId,
			ref: (el: HTMLInputElement | null | any) => {
                field.ref(el);
                if (type === 'number' && el) {
                    numInputRefs.current = el;
                }
            },
			"aria-describedby": helpText && helpId,
			"aria-errormessage": fieldState.error && errorId,
			"aria-invalid": fieldState.invalid,
			isInvalid: fieldState.invalid,
		};
		
		switch (type) {
			case "radio":
				return (
					<Form.Check
						{...commonProps}
						{...(inputAttrs as FormCheckProps)}
						type={type}
						name={field.name}
						className={classNames("d-inline me-2", commonClasses)}
						checked={field.value == inputAttrs.value} // intentional truthy comparison allows for comparing strings and like-numbers.
						onChange={field.onChange}
					/>
				);
			case "checkbox":
			case "switch":
				return (
					<Form.Check
						{...commonProps}
						{...(inputAttrs as FormCheckProps)}
						type={type}
						className={classNames("d-inline mx-2", commonClasses)}
						checked={(field?.value as boolean) || false}
						onChange={field.onChange}
					/>
				);
			case "number":
				return (
					<Form.Control
						{...commonProps}
						{...(inputAttrs as FormNumberInputProps)}
						type="number"
						className={commonClasses}
						value={isNaN(field.value as number) || field.value === null ? "" : (field.value as number)}
						onKeyDown={e => "eE".includes(e.key) && e.preventDefault()} // Prevents type 'e' and 'E' chars
						onChange={e => {
							const parsed = parseFloat(e.target.value);
							let value = isNaN(parsed) ? null : parsed;
							if (value !== null && decimalPlaces !== undefined) value = parseFloat(value.toFixed(decimalPlaces));
							field.onChange(value);
						}}
					/>
				);
			case "tel":
				let { ref, ...props } = commonProps;
				let { mask, ignorerules: _ignorerules, ...telProps } = inputAttrs as FormTelephoneInputProps;
				return (
					<PatternFormat
						{...props}
						{...telProps}
						getInputRef={ref}
						type={type}
						className={commonClasses}
						customInput={Form.Control}
						value={field.value as string}
						format="###-###-####"
						mask={mask ?? "_"}
						onValueChange={v => field.onChange(v.formattedValue == "" ? null : v.formattedValue)}
					/>
				);
			case "currency": {
				let { ref, ...props } = commonProps;
				return (
					<NumericFormat
						{...props}
						{...(inputAttrs as NumericFormatProps)}
						getInputRef={ref}
						type="text"
						className={commonClasses}
						customInput={Form.Control}
						value={field.value !== 0 ? field.value : ""}
						placeholder="$0.00"
						fixedDecimalScale={true}
						thousandSeparator={true}
						decimalScale={2}
						prefix={"$"}
						onValueChange={v => field.onChange(v.value == "" ? 0 : Number(v.value))}
					/>
				);
			}
			case "date": {
				const { serializationFormat, ...datePickerAttrs } = inputAttrs as FormDateInputProps;
				return (
					<DatePicker
						isClearable={true}
						placeholderText="Type date"
						{...commonProps}
						// When using append from useFieldArray there is some funky stuff going on with the autofocus
						ref={ref => {
							if (ref) {
								commonProps.ref({
									focus: ref.setFocus,
								});
							}
						}}
						{...datePickerAttrs}
						className={classNames("form-control", commonClasses)}
						onChange={d => field.onChange(d ? serializeDate(d as Date, serializationFormat) : null)}
						selected={field.value ? deserializeDate(field.value as string, serializationFormat) : undefined}
					/>
				);
			}
			case "textarea":
				const { textAreaAllowEmojis, ...textAreaAttr } = inputAttrs as FormTextAreaInputProps;
				return (
					<Form.Control
						{...commonProps}
						{...textAreaAttr}
						as="textarea"
						className={commonClasses}
						value={field.value == undefined ? "" : (field.value as string)}
						onChange={e => {
							if (textAreaAllowEmojis) {
								field.onChange(e.target.value);
							}
							else {
								const noEmoji = removeEmojis(e.target.value);
								field.onChange(noEmoji == "" ? null : noEmoji);
							}
						}}
						spellCheck={'true'}
					/>
				);
			case "richtext": {
				const { toolbarProps, ...richTextAttrs } = inputAttrs as FormRichTextInputProps;
				//Rich Text Box has multiple levels of components so attributes are split out into sub props.  The richTextAttrs will go on the Slate
				//Component.  The editable props will go on the editor inside the slate component. The toolbar props will go on the toolbar
				//component in the RichTextEditor (the top toolbar)
				return (
					<RichTextBox
						key={field.name}
						onBlur={field.onBlur}
						className={classNames(commonClasses, "form-control")}
						toolbarProps={toolbarProps}
						{...richTextAttrs}
						value={field.value == null ? "" : (field.value as string)}
						onChange={e => {
							let emptyHtml = "<p><span></span></p>";
							let validString = e == "" || e == emptyHtml ? null : e;
							field.onChange(validString);
						}}
					/>
				);
			}
			case "systemtime":
				return (
					<Form.Control
						{...commonProps}
						{...(inputAttrs as FormControlProps)}
						type="time"
						className={commonClasses}
						value={field.value == undefined ? "" : (field.value as string)}
						onChange={e => field.onChange(e.target.value == "" ? (allowEmptyString ? "" : null) : e.target.value)}
					/>
				);
			case "time":
				// eslint-disable-next-line no-case-declarations
				const { timeIntervals = 15, openTime, closeTime, ...rest } = inputAttrs as FormTimeInputProps;
				return (
					<DatePicker
						isClearable={true}
						showTimeSelect
						showTimeSelectOnly
						dateFormat="p"
						placeholderText="00:00 --"
						timeIntervals={timeIntervals}
						{...commonProps}
						{...rest}
						className={classNames("form-control", commonClasses)}
						minTime={DateTime.fromISO(openTime).toJSDate()}
						maxTime={DateTime.fromISO(closeTime).toJSDate()}
						onChange={date => {
							const outOfBoundsDate = getOutOfBoundsDate(date as Date, openTime, closeTime);
							if (outOfBoundsDate) {
								field.onChange(getRoundedDate(timeIntervals, outOfBoundsDate));
							} else {
								field.onChange(date);
							}
						}}
						selected={field?.value ? getRoundedDate(timeIntervals, field.value as Date) : null}
					/>
				);
			case "text":
			default: {
				return (
					<Form.Control
						{...commonProps}
						{...(inputAttrs as FormControlProps)}
						type={type}
						className={commonClasses}
						value={field.value == undefined ? "" : (field.value as string)}
						onChange={e => {
							const noEmoji = removeEmojis(e.target.value);
							field.onChange(noEmoji == "" ? (allowEmptyString ? "" : null) : noEmoji);
						}}
					/>
				);
			}
		}
	};
	return (
		<Controller
			name={fieldName}
			control={control}
			rules={resolvedRules}
			render={({ field, fieldState }) => {
				return (
					<FormGroup
						controlId={resolvedControlId}
						className={`form-input-${type}`}
						controlLabel={inputProps.label}
						helpId={helpId}
						helpText={inputProps.helpText}
						errorId={errorId}
						errorMessage={fieldState.error?.message}
						reverseLabel={reverseLabel}
						controlLabelRowJustifyClass={labelRowJustifyClass}
						controlLabelTailElement={labelTailElement}
						labelPosition={labelPosition}
						labelColumnSmWidth={labelColumnSmWidth}
						inputColumnSmWidth={inputColumnSmWidth}
						labelColumnMdWidth={labelColumnMdWidth}
						inputColumnMdWidth={inputColumnMdWidth}
						inputColumClassName={inputColumClassName}
					>
						{renderInput(field, fieldState)}
					</FormGroup>
				);
			}}
		/>
	);
}
